import * as THREE from 'three';
import { clamp, mulberry32 } from '../world/noise';

type Group = 'ambience' | 'sfx' | 'ui';

// Integrated loudness measured with ffmpeg ebur128 on the ElevenLabs renders; used to normalise at runtime.
const SOUNDS = {
  windBed: { url: 'audio/ambience/wind-bed.mp3', lufs: -38.3, loop: true },
  windStrong: { url: 'audio/ambience/wind-strong.mp3', lufs: -16.9, loop: true },
  insects: { url: 'audio/ambience/insects.mp3', lufs: -33.9, loop: true },
  grass: { url: 'audio/sfx/grass-brush-loop.mp3', lufs: -30.0, loop: true },
  cloth: { url: 'audio/sfx/cloth-flutter-loop.mp3', lufs: -47.8, loop: true },
  gust1: { url: 'audio/sfx/gust-1.mp3', lufs: -41.5 },
  gust2: { url: 'audio/sfx/gust-2.mp3', lufs: -19.4 },
  step1: { url: 'audio/sfx/step-1.mp3', lufs: -46.3 },
  step2: { url: 'audio/sfx/step-2.mp3', lufs: -31.5 },
  step3: { url: 'audio/sfx/step-3.mp3', lufs: -30.0 },
  jump: { url: 'audio/sfx/jump.mp3', lufs: -18.9 },
  land: { url: 'audio/sfx/land.mp3', lufs: -40.0 },
  birds1: { url: 'audio/sfx/birds-1.mp3', lufs: -21.3 },
  birds2: { url: 'audio/sfx/birds-2.mp3', lufs: -18.9 },
  chime: { url: 'audio/sfx/wind-chime.mp3', lufs: -19.6 },
  bell: { url: 'audio/sfx/temple-bell.mp3', lufs: -22.4 },
  guide: { url: 'audio/sfx/guide-wind.mp3', lufs: -14.8 },
  swordDraw: { url: 'audio/sfx/sword-draw.mp3', lufs: -9.8 },
  slash1: { url: 'audio/sfx/sword-slash-1.mp3', lufs: -8.3 },
  slash2: { url: 'audio/sfx/sword-slash-2.mp3', lufs: -14.8 },
  slash3: { url: 'audio/sfx/sword-slash-3.mp3', lufs: -8.1 },
  grassCut1: { url: 'audio/sfx/grass-cut-1.mp3', lufs: -25.1 },
  grassCut2: { url: 'audio/sfx/grass-cut-2.mp3', lufs: -26.7 },
  sheathe: { url: 'audio/sfx/sword-sheathe.mp3', lufs: -12.8 },
  doubleJump: { url: 'audio/sfx/double-jump.mp3', lufs: -15.1 },
  flipCloth: { url: 'audio/sfx/flip-cloth.mp3', lufs: -18.1 },
  uiHover: { url: 'audio/ui/ui-hover.mp3', lufs: -23.7 },
  uiConfirm: { url: 'audio/ui/ui-confirm.mp3', lufs: -30.6 },
} as const;

export type SoundName = keyof typeof SOUNDS;
const TARGET_LUFS = -20;

type Clip = { buffer: AudioBuffer; gain: number; start: number; end: number };

type LoopVoice = {
  source: AudioBufferSourceNode;
  gain: GainNode;
  filter: BiquadFilterNode;
  pan: StereoPannerNode;
};

export type AudioSettings = { master: number; ambience: number; sfx: number };

const tmpForward = new THREE.Vector3();
const tmpUp = new THREE.Vector3();

export class AudioManager {
  readonly ctx: AudioContext;
  private readonly master: GainNode;
  private readonly groups: Record<Group, GainNode>;
  private readonly clips = new Map<SoundName, Clip>();
  private readonly loops = new Map<string, LoopVoice>();
  private readonly rng = mulberry32(4242);
  private started = false;
  private gustCooldown = 4;
  private birdTimer = 6;
  private lastGust = 0;
  private lastStep = -1;
  private duck = 1;
  settings: AudioSettings = { master: 0.85, ambience: 0.9, sfx: 0.9 };
  loadedCount = 0;
  readonly totalCount = Object.keys(SOUNDS).length;

  constructor() {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctor({ latencyHint: 'interactive' });
    const compressor = this.ctx.createDynamicsCompressor();
    compressor.threshold.value = -14;
    compressor.knee.value = 12;
    compressor.ratio.value = 3;
    compressor.attack.value = 0.01;
    compressor.release.value = 0.3;
    this.master = this.ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(compressor).connect(this.ctx.destination);
    const mk = () => {
      const g = this.ctx.createGain();
      g.connect(this.master);
      return g;
    };
    this.groups = { ambience: mk(), sfx: mk(), ui: mk() };
    this.applySettings();
  }

  async load(base: string, onProgress?: (fraction: number) => void): Promise<void> {
    const entries = Object.entries(SOUNDS) as Array<[SoundName, (typeof SOUNDS)[SoundName]]>;
    await Promise.all(
      entries.map(async ([name, def]) => {
        try {
          const response = await fetch(`${base}${def.url}`);
          const data = await response.arrayBuffer();
          const buffer = await this.ctx.decodeAudioData(data);
          const loop = 'loop' in def && def.loop;
          const { start, end } = loop ? this.trimPadding(buffer) : this.trimLeading(buffer);
          this.clips.set(name, { buffer, gain: Math.pow(10, (TARGET_LUFS - def.lufs) / 20), start, end });
        } catch (error) {
          console.warn(`Audio failed to load: ${name}`, error);
        }
        this.loadedCount++;
        onProgress?.(this.loadedCount / this.totalCount);
      }),
    );
  }

  /** Skips MP3 encoder padding so loops are gapless. */
  private trimPadding(buffer: AudioBuffer): { start: number; end: number } {
    const data = buffer.getChannelData(0);
    let s = 0;
    while (s < data.length && Math.abs(data[s]) < 1e-4) s++;
    let e = data.length - 1;
    while (e > s && Math.abs(data[e]) < 1e-4) e--;
    return { start: s / buffer.sampleRate, end: (e + 1) / buffer.sampleRate };
  }

  /** Starts one-shots at their first audible transient. */
  private trimLeading(buffer: AudioBuffer): { start: number; end: number } {
    const data = buffer.getChannelData(0);
    let s = 0;
    while (s < data.length && Math.abs(data[s]) < 0.003) s++;
    return { start: Math.max(0, s - 64) / buffer.sampleRate, end: buffer.duration };
  }

  async unlock(): Promise<void> {
    if (this.ctx.state !== 'running') await this.ctx.resume().catch(() => {});
    if (!this.started) {
      this.started = true;
      this.startLoops();
    }
    this.fadeMaster(this.settings.master, 2.5);
  }

  get running(): boolean {
    return this.ctx.state === 'running';
  }

  private fadeMaster(value: number, seconds: number): void {
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value, now);
    this.master.gain.linearRampToValueAtTime(value, now + seconds);
  }

  applySettings(): void {
    this.groups.ambience.gain.value = this.settings.ambience;
    this.groups.sfx.gain.value = this.settings.sfx;
    this.groups.ui.gain.value = this.settings.sfx * 0.8;
    if (this.started) this.fadeMaster(this.settings.master * this.duck, 0.2);
  }

  setDucked(ducked: boolean): void {
    this.duck = ducked ? 0.35 : 1;
    if (this.started) this.fadeMaster(this.settings.master * this.duck, 0.6);
  }

  private startLoops(): void {
    const make = (key: string, name: SoundName, filterType: BiquadFilterType, freq: number) => {
      const clip = this.clips.get(name);
      if (!clip) return;
      const source = this.ctx.createBufferSource();
      source.buffer = clip.buffer;
      source.loop = true;
      source.loopStart = clip.start;
      source.loopEnd = clip.end;
      const filter = this.ctx.createBiquadFilter();
      filter.type = filterType;
      filter.frequency.value = freq;
      filter.Q.value = 0.5;
      const pan = this.ctx.createStereoPanner();
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      source.connect(filter).connect(pan).connect(gain).connect(this.groups.ambience);
      source.start(this.ctx.currentTime + 0.05, clip.start + this.rng() * (clip.end - clip.start) * 0.5);
      this.loops.set(key, { source, gain, filter, pan });
    };
    make('windBed', 'windBed', 'lowpass', 2400);
    make('windStrong', 'windStrong', 'lowpass', 900);
    make('insects', 'insects', 'highpass', 1200);
    make('grass', 'grass', 'highpass', 300);
    make('cloth', 'cloth', 'bandpass', 1400);
  }

  private setLoop(key: string, gain: number, filterFreq?: number, pan?: number, rate?: number): void {
    const voice = this.loops.get(key);
    if (!voice) return;
    const now = this.ctx.currentTime;
    const clip = key in SOUNDS ? this.clips.get(key as SoundName) : undefined;
    voice.gain.gain.setTargetAtTime(gain * (clip?.gain ?? 1), now, 0.12);
    if (filterFreq !== undefined) voice.filter.frequency.setTargetAtTime(filterFreq, now, 0.2);
    if (pan !== undefined) voice.pan.pan.setTargetAtTime(pan, now, 0.3);
    if (rate !== undefined) voice.source.playbackRate.setTargetAtTime(rate, now, 0.2);
  }

  /** `offset` skips into the clip (seconds) so a transient can land on an animation beat. */
  play(name: SoundName, options: { volume?: number; rate?: number; pan?: number; group?: Group; delay?: number; offset?: number } = {}): void {
    const clip = this.clips.get(name);
    if (!clip || !this.started) return;
    const start = Math.min(clip.start + (options.offset ?? 0), clip.end - 0.01);
    const source = this.ctx.createBufferSource();
    source.buffer = clip.buffer;
    source.playbackRate.value = options.rate ?? 1;
    const gain = this.ctx.createGain();
    gain.gain.value = (options.volume ?? 1) * clip.gain;
    let node: AudioNode = source;
    if (options.pan !== undefined) {
      const pan = this.ctx.createStereoPanner();
      pan.pan.value = clamp(options.pan, -1, 1);
      node.connect(pan);
      node = pan;
    }
    node.connect(gain).connect(this.groups[options.group ?? 'sfx']);
    source.start(this.ctx.currentTime + (options.delay ?? 0), start, clip.end - start);
  }

  playAt(name: SoundName, position: THREE.Vector3, options: { volume?: number; rate?: number; ref?: number } = {}): void {
    const clip = this.clips.get(name);
    if (!clip || !this.started) return;
    const source = this.ctx.createBufferSource();
    source.buffer = clip.buffer;
    source.playbackRate.value = options.rate ?? 1;
    const panner = this.ctx.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = options.ref ?? 6;
    panner.rolloffFactor = 1.1;
    panner.maxDistance = 400;
    panner.positionX.value = position.x;
    panner.positionY.value = position.y;
    panner.positionZ.value = position.z;
    const gain = this.ctx.createGain();
    gain.gain.value = (options.volume ?? 1) * clip.gain;
    source.connect(panner).connect(gain).connect(this.groups.sfx);
    source.start(this.ctx.currentTime, clip.start, clip.end - clip.start);
  }

  footstep(intensity: number): void {
    const options: SoundName[] = ['step1', 'step2', 'step3'];
    let pick = Math.floor(this.rng() * options.length);
    if (pick === this.lastStep) pick = (pick + 1) % options.length;
    this.lastStep = pick;
    this.play(options[pick], { volume: 0.32 * intensity, rate: 0.92 + this.rng() * 0.16, pan: (this.rng() - 0.5) * 0.2 });
  }

  /**
   * Drives the continuous layers from the simulation.
   * windFrom is the horizontal direction the wind comes from in camera space (x = right).
   */
  updateAmbience(
    dt: number,
    state: { gust: number; windStrength: number; windPanX: number; speed: number; sprint: number; guide: number; grounded: boolean; inGrass: number },
    camera: THREE.Camera,
  ): void {
    if (!this.started) return;
    const g = state.gust;
    const w = state.windStrength;
    const listener = this.ctx.listener;
    camera.getWorldDirection(tmpForward);
    tmpUp.set(0, 1, 0).applyQuaternion(camera.quaternion);
    const p = camera.position;
    if (listener.positionX) {
      const now = this.ctx.currentTime;
      listener.positionX.setValueAtTime(p.x, now);
      listener.positionY.setValueAtTime(p.y, now);
      listener.positionZ.setValueAtTime(p.z, now);
      listener.forwardX.setValueAtTime(tmpForward.x, now);
      listener.forwardY.setValueAtTime(tmpForward.y, now);
      listener.forwardZ.setValueAtTime(tmpForward.z, now);
      listener.upX.setValueAtTime(tmpUp.x, now);
      listener.upY.setValueAtTime(tmpUp.y, now);
      listener.upZ.setValueAtTime(tmpUp.z, now);
    }

    const pan = clamp(state.windPanX * 0.45, -0.6, 0.6);
    this.setLoop('windBed', 0.55 + w * 0.35 + g * 0.2, 1400 + g * 3200 + state.guide * 2500, pan * 0.6);
    this.setLoop('windStrong', 0.04 + Math.pow(g, 1.6) * 0.34 * w + state.guide * 0.35 + state.sprint * 0.05, 500 + g * 2600 + state.guide * 2000, pan);
    this.setLoop('insects', 0.16 * (1 - g * 0.6));
    const moving = clamp(state.speed / 5, 0, 1.3) * (state.grounded ? 1 : 0.2) * state.inGrass;
    this.setLoop('grass', moving * 0.42, 500 + moving * 900, 0, 0.85 + moving * 0.3);
    this.setLoop('cloth', clamp(state.speed / 6, 0, 1) * 0.28 + g * w * 0.12, 1100 + state.speed * 180, 0, 0.9 + state.sprint * 0.2);

    // One-shot gust swells when a visible wave rolls over the player.
    this.gustCooldown -= dt;
    if (g > 0.62 && this.lastGust <= 0.62 && this.gustCooldown <= 0) {
      this.play(this.rng() < 0.5 ? 'gust1' : 'gust2', { volume: 0.35 + g * 0.25, pan, group: 'ambience', rate: 0.94 + this.rng() * 0.1 });
      this.gustCooldown = 7 + this.rng() * 6;
    }
    this.lastGust = g;

    this.birdTimer -= dt;
    if (this.birdTimer <= 0) {
      const angle = this.rng() * Math.PI * 2;
      const dist = 25 + this.rng() * 45;
      const pos = new THREE.Vector3(p.x + Math.cos(angle) * dist, p.y + 8 + this.rng() * 18, p.z + Math.sin(angle) * dist);
      this.playAt(this.rng() < 0.5 ? 'birds1' : 'birds2', pos, { volume: 0.5, ref: 14, rate: 0.95 + this.rng() * 0.1 });
      this.birdTimer = 9 + this.rng() * 14;
    }
  }
}
